// SYSTEM-001 console batch 3 (owner, 2026-10-25; D-067): switches (kill switches and gradual rollout
// for every plan feature and a few platform operations), service and integration health, every
// storefront with its domains, and the theme catalog. The switch rules are pure here so the server
// (ady-api's switch gate) and the dev mocks decide the same way.
import { PLAN_FEATURES } from "./plan-features";
import type { PlanIcon } from "./plan-features";

// ---------- switches ----------

export interface SwitchDef {
  key: string;
  kind: "feature" | "operation";
  /** Vendor route-ID exact IDs or prefixes it stops while off. */
  routes: readonly string[];
  /** Admin nav items that show "paused". */
  nav: readonly string[];
  /** Operations stop writes only; a paused feature stops its pages too. */
  block: "writes" | "all";
}

/** Platform operations the owner can pause for everyone (or some shops). */
const OPERATIONS: SwitchDef[] = [
  { key: "signups", kind: "operation", routes: ["org.create"], nav: [], block: "writes" },
  {
    key: "plan_checkout",
    kind: "operation",
    routes: ["billing.checkout"],
    nav: [],
    block: "writes",
  },
  {
    key: "wallet_topups",
    kind: "operation",
    routes: ["wallet.topups.create"],
    nav: [],
    block: "writes",
  },
  {
    key: "service_purchases",
    kind: "operation",
    routes: ["ai.credits.buy", "themes.premium.buy", "billing.addons.purchase"],
    nav: [],
    block: "writes",
  },
];

/** Every switch: the operations, then one per plan feature that has routes or pages. */
export const SWITCHES: readonly SwitchDef[] = [
  ...OPERATIONS,
  ...PLAN_FEATURES.filter((d) => d.kind === "flag" && (d.routes?.length || d.nav?.length)).map(
    (d): SwitchDef => ({
      key: d.key,
      kind: "feature",
      routes: d.routes ?? [],
      nav: d.nav ?? [],
      block: "all",
    }),
  ),
];
export const SWITCH_BY_KEY = new Map(SWITCHES.map((s) => [s.key, s]));
/** `feature_flags.key` of a switch (SYSTEM-04 rollout flags, migration 0105). */
export const switchFlagKey = (key: string) => `switch:${key}`;

/** The switch that stops a route, if any. */
export function switchForRoute(routeId: string): SwitchDef | undefined {
  return SWITCHES.find((s) =>
    s.routes.some((r) => routeId === r || (r.endsWith(".") && routeId.startsWith(r))),
  );
}

export interface SwitchRules {
  /** null = no rule (on). */
  global: boolean | null;
  /** plan key → on/off. */
  plans: Record<string, boolean>;
  /** org ID → on/off. */
  orgs: Record<string, boolean>;
  /** Gradual rollout: this share of shops gets `value`, the rest the opposite. */
  percent: { pct: number; value: boolean } | null;
}

export const NO_RULES: SwitchRules = { global: null, plans: {}, orgs: {}, percent: null };

/** Deterministic 0–99 bucket per shop and switch (FNV-1a, as SYSTEM-04 flags). */
export function rolloutBucket(orgId: string, flagKey: string): number {
  let h = 0x811c9dc5;
  const s = `${orgId}:${flagKey}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h % 100;
}

/** On for this shop? Order: the shop's own rule > its plan > rollout > everyone > on. */
export function switchOn(
  key: string,
  rules: SwitchRules,
  ctx: { orgId?: string | null; planKey?: string | null },
): boolean {
  if (ctx.orgId && rules.orgs[ctx.orgId] !== undefined) return rules.orgs[ctx.orgId]!;
  if (ctx.planKey && rules.plans[ctx.planKey] !== undefined) return rules.plans[ctx.planKey]!;
  if (rules.percent && ctx.orgId) {
    const inside = rolloutBucket(ctx.orgId, switchFlagKey(key)) < rules.percent.pct;
    return inside ? rules.percent.value : !rules.percent.value;
  }
  if (rules.global !== null) return rules.global;
  return true;
}

export type SwitchState = "on" | "off" | "partial";

export function switchState(r: SwitchRules): SwitchState {
  const values = [
    ...Object.values(r.orgs),
    ...Object.values(r.plans),
    ...(r.percent ? [r.percent.value, !r.percent.value] : []),
  ];
  const base = r.global ?? true;
  if (values.length === 0) return base ? "on" : "off";
  return values.every((v) => v === base) ? (base ? "on" : "off") : "partial";
}

export interface SwitchView {
  key: string;
  kind: SwitchDef["kind"];
  /** The plan feature group, for features. */
  group: string | null;
  rules: SwitchRules;
  state: SwitchState;
  /** Shops this switch is off for now (not closed). */
  off_shops: number;
  note: string | null;
  updated_at: string | null;
}

export interface SwitchBoard {
  rows: SwitchView[];
  plans: { key: string; name: string; icon: PlanIcon }[];
  shops: { id: string; name: string }[];
  paused: number;
}

// ---------- health ----------

export interface ServiceCheckView {
  key: string;
  target: string;
  status: "up" | "down" | "unknown";
  last_at: string | null;
  latency_ms: number | null;
  error: string | null;
  /** Percent (0–100) of counted checks that were up. */
  uptime_30d: number | null;
}

export interface IncidentView {
  id: string;
  title: string;
  state: "investigating" | "identified" | "monitoring" | "resolved";
  impact: "minor" | "major";
  started_at: string | null;
  resolved_at: string | null;
  public_note: string;
  updated_at: string | null;
}

/** One integration across every shop (platform database: Daraz, Sampark channels, webhooks, jobs). */
export interface IntegrationHealthRow {
  key: string;
  kind: "marketplace" | "channel" | "webhook" | "jobs";
  connected: number;
  problems: number;
  /** Shops with a problem (names only) and what it is. */
  issues: { org_id: string; org_name: string; problem: string; at: string | null }[];
}

export interface HealthBoard {
  checks: ServiceCheckView[];
  incidents: IncidentView[];
  integrations: IntegrationHealthRow[];
  checked_at: string | null;
}

/** One shop's payment methods and couriers (its own database, read on its page). */
export interface ShopIntegrations {
  payments: { provider: string; enabled: boolean; mode: string; state: string }[];
  couriers: { courier: string; mode: string; state: string }[];
  channels: { channel: string; connected: boolean }[];
  marketplaces: { marketplace: string; state: string; next_sync_at: string | null }[];
}

// ---------- storefronts ----------

export interface HostView {
  hostname: string;
  kind: "platform" | "custom";
  canonical: boolean;
  ssl_status: string | null;
  verify_status: string | null;
  last_error: string | null;
  checked_at: string | null;
}

export interface StorefrontRow {
  store_id: string;
  org_id: string;
  org_name: string;
  org_lifecycle: string;
  name: string;
  slug: string;
  status: "draft" | "coming_soon" | "password" | "live" | "suspended";
  canonical_host: string;
  hosts: HostView[];
  theme_key: string | null;
  theme_name: string | null;
  /** A custom domain that is not active yet, or has an error. */
  domain_problem: boolean;
  created_at: string | null;
}

export interface StorefrontBoard {
  rows: StorefrontRow[];
  counts: Record<StorefrontRow["status"], number> & {
    custom_domains: number;
    domain_problems: number;
  };
}

// ---------- theme catalog ----------

export type ThemeCatalogStatus = "draft" | "published" | "retired";

export interface ThemeCatalogRow {
  key: string;
  name: string;
  /** The newest version (an uploaded theme can have a newer draft than the live one). */
  version: string;
  summary: string | null;
  layout: string | null;
  colors: string[];
  /** For shops: published while any version is live; else the newest version's status. */
  status: ThemeCatalogStatus;
  /** D-091: comes with Adyvance (in code) or the owner uploaded it in the console. */
  source: "built_in" | "uploaded";
  /** D-091: the card picture of the newest version (uploaded themes), or null. */
  preview_url: string | null;
  /** The version shops get now, or null when none is live. */
  live_version: string | null;
  /** Every version, newest first. */
  versions: { version: string; status: ThemeCatalogStatus; updated_at: string | null }[];
  updated_at: string | null;
  price_paisa: number | null;
  period: "once" | "yearly";
  premium: boolean;
  /** Stores whose live theme it is. */
  live_stores: number;
  licences: {
    org_id: string;
    org_name: string;
    source: "purchase" | "grant";
    ends_at: string | null;
  }[];
  /** D-093: Classic Minimal (`DEFAULT_THEME_KEY`): installed on every new shop, never deleted. */
  is_default: boolean;
  /** D-093: a built-in theme the owner deleted (hidden from shops; Restore brings it back). */
  deleted: boolean;
  /** D-093: the newest version's files from its .zip (`images/…`), served by the files route. */
  files: string[];
  /** D-093: the newest version's package has a preview block (photos, sample products). */
  has_preview: boolean;
  /** D-093: stores whose live theme or current install is this theme: the shops a delete moves. */
  stores_using: number;
}

export interface ThemeCatalogBoard {
  rows: ThemeCatalogRow[];
  shops: { id: string; name: string }[];
}

/** D-093: DELETE /themes/:themeKey — the board after the delete and the stores moved to Classic Minimal. */
export interface ThemeDeleteResult {
  board: ThemeCatalogBoard;
  moved: number;
}
